import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, Download, Upload, Database, AlertCircle, Loader2, ListOrdered, Pencil, Save, X, Shield, UserPlus, Lock, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { apiFetch } from '../lib/api';

const Settings = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const fileInputRef = useRef(null);
  const [accountLimits, setAccountLimits] = useState(null);

  // Phases
  const [phases, setPhases] = useState([]);
  const [phasesLoading, setPhasesLoading] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingName, setEditingName] = useState('');

  // User management
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', password: '' });

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUser(user);
    const lastBackupInfo = localStorage.getItem('lastBackupDate');
    if (lastBackupInfo) setLastBackup(lastBackupInfo);
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      fetchUsers();
      if (!isSuperAdmin) fetchPhases();
      fetchAccountLimits();
    }
  }, [currentUser?.id]);

  const fetchAccountLimits = async () => {
    try {
      const res = await apiFetch('/api/account/limits');
      if (res.ok) setAccountLimits(await res.json());
    } catch (e) { console.error(e); }
  };

  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  // ---- Users ----
  const fetchUsers = async () => {
    try {
      const response = await apiFetch('/api/auth/users');
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      flash('error', 'Tous les champs sont requis');
      return;
    }
    try {
      const response = await apiFetch('/api/auth/users', {
        method: 'POST',
        body: JSON.stringify({
          name: newUser.name,
          email: newUser.email,
          password: newUser.password,
          role: isSuperAdmin ? 'admin' : 'collaborateur'
        })
      });
      if (response.ok) {
        flash('success', `${isSuperAdmin ? 'Administrateur' : 'Collaborateur'} créé`);
        setShowAddUser(false);
        setNewUser({ name: '', email: '', password: '', role: '' });
        fetchUsers();
      } else {
        const data = await response.json();
        flash('error', data.detail || 'Erreur');
      }
    } catch (error) {
      flash('error', 'Erreur de connexion');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      const body = { name: editUserData.name, email: editUserData.email };
      if (editUserData.password) body.password = editUserData.password;

      const response = await apiFetch(`/api/auth/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      if (response.ok) {
        flash('success', 'Utilisateur modifié');
        setEditingUser(null);
        fetchUsers();
      } else {
        const data = await response.json();
        flash('error', data.detail || 'Erreur');
      }
    } catch (error) {
      flash('error', 'Erreur de connexion');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser.id) {
      flash('error', 'Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      const response = await apiFetch(`/api/auth/users/${userId}`, { method: 'DELETE' });
      if (response.ok) {
        flash('success', 'Utilisateur supprimé');
        fetchUsers();
      } else {
        const data = await response.json();
        flash('error', data.detail || 'Erreur');
      }
    } catch (error) {
      flash('error', 'Erreur de connexion');
    }
  };

  // ---- Phases ----
  const fetchPhases = async () => {
    try {
      const response = await apiFetch('/api/settings/phases');
      const data = await response.json();
      setPhases(data.phases || []);
    } catch (error) {
      console.error('Error fetching phases:', error);
    }
  };

  const savePhases = async (updatedPhases) => {
    setPhasesLoading(true);
    try {
      const response = await apiFetch('/api/settings/phases', {
        method: 'PUT',
        body: JSON.stringify({ phases: updatedPhases })
      });
      if (response.ok) {
        setPhases(updatedPhases);
        flash('success', 'Étapes mises à jour');
      } else {
        flash('error', 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      flash('error', 'Erreur de connexion');
    }
    setPhasesLoading(false);
  };

  const handleAddPhase = () => {
    if (!newPhaseName.trim()) return;
    savePhases([...phases, newPhaseName.trim()]);
    setNewPhaseName('');
  };

  const handleRemovePhase = (index) => savePhases(phases.filter((_, i) => i !== index));

  const handleStartEdit = (index) => { setEditingIndex(index); setEditingName(phases[index]); };

  const handleSaveEdit = () => {
    if (!editingName.trim() || editingIndex === null) return;
    const updated = [...phases];
    updated[editingIndex] = editingName.trim();
    setEditingIndex(null);
    savePhases(updated);
  };

  // ---- Backup ----
  const handleExportBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await apiFetch('/api/backup/export');
      const data = await response.json();
      if (response.ok) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `creaflow-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        const now = new Date().toLocaleString('fr-FR');
        localStorage.setItem('lastBackupDate', now);
        setLastBackup(now);
        flash('success', 'Sauvegarde exportée');
      }
    } catch (error) {
      flash('error', 'Erreur de connexion');
    }
    setBackupLoading(false);
  };

  const handleRestoreBackup = async (event, merge = false) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!merge && !window.confirm('ATTENTION: Cette action va remplacer TOUTES vos données actuelles. Continuer ?')) {
      event.target.value = '';
      return;
    }
    setRestoreLoading(true);
    try {
      const fileContent = await file.text();
      const backupData = JSON.parse(fileContent);
      const response = await apiFetch('/api/backup/restore', {
        method: 'POST',
        body: JSON.stringify({ data: backupData, merge })
      });
      const result = await response.json();
      if (response.ok) {
        flash('success', `Restauration réussie !`);
      } else {
        flash('error', result.detail || 'Erreur lors de la restauration');
      }
    } catch (error) {
      flash('error', 'Fichier invalide ou corrompu');
    }
    event.target.value = '';
    setRestoreLoading(false);
  };

  if (!currentUser) return <div className="p-6">Chargement...</div>;

  const usersLabel = isSuperAdmin ? 'Administrateurs' : 'Mon compte';
  const canManageUsers = isSuperAdmin || isAdmin;
  const isFreeAccount = accountLimits?.account_type === 'free';
  const canCreateCollaborator = !isFreeAccount || isSuperAdmin;
  const canBackup = !isFreeAccount || isSuperAdmin;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="page-title">Paramètres</h1>
        <p className="text-base text-muted-foreground mt-2">Configurez votre application</p>
      </div>

      {message.text && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}`}>
          {message.text}
        </motion.div>
      )}

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            {usersLabel}
          </TabsTrigger>
          {!isSuperAdmin && (
            <TabsTrigger value="phases">
              <ListOrdered className="w-4 h-4 mr-2" />
              Étapes
            </TabsTrigger>
          )}
          {!isSuperAdmin && (
            <TabsTrigger value="backup" disabled={!canBackup}>
              {!canBackup && <Lock className="w-3 h-3 mr-1" />}
              <Database className="w-4 h-4 mr-2" />
              Sauvegarde
            </TabsTrigger>
          )}
        </TabsList>

        {/* ========= USERS TAB ========= */}
        <TabsContent value="users">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">{usersLabel}</h2>
                <p className="text-sm text-muted-foreground">
                  {isSuperAdmin
                    ? 'Gérez les comptes administrateurs de vos clients'
                    : isAdmin
                    ? 'Gérez les accès de votre équipe'
                    : 'Informations de votre compte'}
                </p>
              </div>
              {canManageUsers && (
                canCreateCollaborator ? (
                  <Button onClick={() => setShowAddUser(true)} data-testid="add-user-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    {isSuperAdmin ? 'Nouvel admin' : 'Nouveau collaborateur'}
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => navigate('/pricing')} className="text-muted-foreground" data-testid="add-user-btn-disabled">
                    <Lock className="w-4 h-4 mr-2" />
                    Nouveau collaborateur
                  </Button>
                )
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Statut</TableHead>
                  {canManageUsers && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.filter(u => u.id !== currentUser.id || !isSuperAdmin).map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'superadmin' ? 'default' : user.role === 'admin' ? 'default' : 'outline'}>
                        {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Collaborateur'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="default" className={user.is_active !== false ? 'bg-green-500' : 'bg-gray-400'}>
                        {user.is_active !== false ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    {canManageUsers && (
                      <TableCell className="text-right space-x-1">
                        {user.role !== 'superadmin' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setEditingUser(user);
                              setEditUserData({ name: user.name, email: user.email, password: '' });
                            }} data-testid={`edit-user-${user.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteUser(user.id)} data-testid={`delete-user-${user.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {/* Show current superadmin separately at bottom */}
                {isSuperAdmin && (
                  <TableRow>
                    <TableCell className="font-medium">{currentUser.name}</TableCell>
                    <TableCell>{currentUser.email}</TableCell>
                    <TableCell><Badge>Super Admin</Badge></TableCell>
                    <TableCell><Badge className="bg-green-500">Actif</Badge></TableCell>
                    {canManageUsers && <TableCell></TableCell>}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ========= PHASES TAB ========= */}
        {!isSuperAdmin && (
          <TabsContent value="phases">
            <Card className="p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold">Étapes de la Timeline</h2>
                <p className="text-sm text-muted-foreground">Personnalisez les étapes utilisées pour les nouveaux projets</p>
              </div>
              <div className="space-y-2 mb-4">
                {phases.map((phase, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 border rounded-lg bg-background" data-testid={`phase-item-${index}`}>
                    <span className="text-xs text-muted-foreground w-6 text-center">{index + 1}</span>
                    {editingIndex === index ? (
                      <div className="flex-1 flex items-center gap-2">
                        <Input value={editingName} onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()} className="flex-1 h-8" autoFocus />
                        <Button size="sm" variant="outline" onClick={handleSaveEdit}>OK</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingIndex(null)}>Annuler</Button>
                      </div>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{phase}</span>
                        <Button size="sm" variant="ghost" onClick={() => handleStartEdit(index)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRemovePhase(index)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Nouvelle étape..." value={newPhaseName} onChange={(e) => setNewPhaseName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPhase()} className="flex-1" data-testid="phase-new-input" />
                <Button onClick={handleAddPhase} disabled={!newPhaseName.trim() || phasesLoading} data-testid="phase-add-btn">
                  <Plus className="w-4 h-4 mr-1" />Ajouter
                </Button>
              </div>
            </Card>
          </TabsContent>
        )}

        {/* ========= BACKUP TAB ========= */}
        {!isSuperAdmin && (
          <TabsContent value="backup">
            {!canBackup ? (
              <Card className="p-6 border-amber-200 bg-amber-50">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Fonctionnalité réservée au plan Pro</p>
                    <p className="text-sm text-amber-700">La sauvegarde et restauration de données sont disponibles avec l'abonnement Pro.</p>
                  </div>
                  <Button size="sm" onClick={() => navigate('/pricing')} className="flex-shrink-0 ml-auto" data-testid="upgrade-btn-backup">
                    Passer au Pro <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </Card>
            ) : (
            <Card className="p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold">Sauvegarde & Restauration</h2>
                <p className="text-sm text-muted-foreground">Exportez ou restaurez vos données</p>
              </div>
              <div className="space-y-6">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Download className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium">Exporter les données</p>
                      <p className="text-sm text-muted-foreground">Téléchargez une copie de toutes vos données</p>
                    </div>
                  </div>
                  {lastBackup && <p className="text-xs text-muted-foreground mb-3">Dernière sauvegarde: {lastBackup}</p>}
                  <Button onClick={handleExportBackup} disabled={backupLoading} className="w-full sm:w-auto">
                    {backupLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Export en cours...</> : <><Download className="w-4 h-4 mr-2" />Télécharger la sauvegarde</>}
                  </Button>
                </div>
                <div className="p-4 border rounded-lg border-orange-200 bg-orange-50">
                  <div className="flex items-center gap-3 mb-3">
                    <Upload className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-700">Restaurer les données</p>
                      <p className="text-sm text-orange-600">Importez un fichier de sauvegarde précédent</p>
                    </div>
                  </div>
                  <div className="bg-orange-100 border border-orange-300 rounded p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-orange-700"><strong>Attention:</strong> La restauration complète remplacera TOUTES vos données actuelles.</p>
                    </div>
                  </div>
                  <input type="file" accept=".json" ref={fileInputRef} onChange={(e) => handleRestoreBackup(e, false)} className="hidden" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={restoreLoading} className="w-full sm:w-auto border-orange-300 text-orange-700 hover:bg-orange-100">
                    {restoreLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Restauration...</> : <><Upload className="w-4 h-4 mr-2" />Restaurer depuis un fichier</>}
                  </Button>
                </div>
              </div>
            </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* ========= ADD USER DIALOG ========= */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isSuperAdmin ? 'Nouvel administrateur' : 'Nouveau collaborateur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium mb-1">Nom</label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Nom complet" data-testid="new-user-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="email@exemple.com" data-testid="new-user-email" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mot de passe</label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Minimum 6 caractères" data-testid="new-user-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Annuler</Button>
            <Button onClick={handleAddUser} data-testid="confirm-add-user">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========= EDIT USER DIALOG ========= */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium mb-1">Nom</label>
              <Input value={editUserData.name} onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })} data-testid="edit-user-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input type="email" value={editUserData.email} onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })} data-testid="edit-user-email" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Nouveau mot de passe (laisser vide pour ne pas changer)</label>
              <Input type="password" value={editUserData.password} onChange={(e) => setEditUserData({ ...editUserData, password: e.target.value })} placeholder="Laisser vide" data-testid="edit-user-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Annuler</Button>
            <Button onClick={handleUpdateUser} data-testid="confirm-edit-user">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
